import { nextTick, ref, watch } from 'vue'
import type { Character, Chat, Message, SpritePosition } from '@/types'
import { generateUuid } from '@/utils/generateUuid'
import { Queue } from '@/utils/Queue'
import type { GetCharResponsesPayload } from '@/services/chat'
import { chatService } from '@/services/chat'
import { mapMessageToXML, textToMessagesNew } from '@/utils/textToMessages'
import { useDebugStore } from '@/store/debug'
import { checkMsgIdExist, getAvatarsPosition } from '@/utils/poseUtil'
import { isCloseTag } from '@/utils/stringUtil'
import { sleep } from '@/utils/baseUtil'
import { uniqueArray } from '@/utils/uniqueArray'
import type { GenerateEventPayload } from '@/types/event'

export function useStream() {
  const debugStore = useDebugStore()

  const messages = ref<Message[]>([])
  const avatarPositions = ref<(SpritePosition | undefined)[]>([])
  const TEXT_DEQUEUE_INTERVAL_LONG = 1000

  const initMsgStreamDelayTimer = ref<number>()

  const originalTexts = ref<string>()
  // 利用stream已完成的字段提前加载音频
  const preloadedAudios = ref<HTMLAudioElement[]>([])
  watch(originalTexts, (val) => {
    if (val) {
      const audioRegex = /<say audio_url=\"([^"]*)\">/g
      const audioUrls: string[] = []
      let match
      for (
        match = audioRegex.exec(val);
        match !== null;
        match = audioRegex.exec(val)
      ) {
        audioUrls.push(match[1])
      }
      const uniqueAudioUrls = uniqueArray(audioUrls)
      if (uniqueAudioUrls.length > 0) {
        uniqueAudioUrls.forEach((audioUrl) => {
          const audio = new Audio(audioUrl)
          audio.load()

          preloadedAudios.value.push(audio)
        })
      }
    }
  })
  // Used for fetch the suggest response in advance, but prevent duplicate message
  const lastMessagePosition = ref<number>(0)

  // if autoPlay next message
  const autoPlay = ref(true)
  function setAudioPlay(value: boolean) {
    autoPlay.value = value
  }
  // 监听autoplay是否resume
  watch(autoPlay, (val) => {
    if (val) {
      resume(true)
    }
  })

  const timerId = ref<number>(0)

  // the queue for receiving the message
  const queue = ref<Queue<string>>()
  // waiting for the message response
  const loading = ref<boolean>(false)
  // done response but still stream
  const responseDone = ref<boolean>(false)

  const finalText = ref<string>('')
  const chatRoundEnd = ref<boolean>(false)

  const isPause = ref<boolean>(false)
  const hasError = ref<boolean>(false)

  // use for resume the stream
  const lastMessageIdPrefix = ref<string>('')
  const lastOriginalMessages = ref<Message[]>([])
  const lastChat = ref<Chat>()

  const audioPlaying = ref<boolean>(false)
  const isStreaming = ref<boolean>(false)

  // flag that make the stream directly to the end of the current message
  const skipToNextMsg = ref(false)
  const lockTap = ref<boolean>(false)
  const afterSkipCallback = ref<() => void>()

  function pause() {
    console.debug('[useStream] pause')
    clearTimeout(timerId.value)
    clearTimeout(initMsgStreamDelayTimer.value)
    isPause.value = true
  }

  function resume(
    // force to resume the stream no matter the autoPlay status
    force: boolean = false,
  ) {
    isPause.value = false
    console.debug('[useStream] resume')
    if (!lastChat.value || lastChat.value?.queue?.isEmpty()) {
      resetSavedInfo()
      return
    }
    if (autoPlay.value || force) {
      dequeue(
        lastChat.value!,
        lastMessageIdPrefix.value,
        lastOriginalMessages.value,
      )
    }
  }

  function resetSavedInfo() {
    console.debug('[useStream] resetSavedInfo')
    lastChat.value = undefined
    lastMessageIdPrefix.value = ''
    lastOriginalMessages.value = []
  }

  function initMessages(newMsg: Message[]) {
    messages.value.push(...newMsg)
  }

  function generateUserMessage(text: string, char: Character) {
    // Append user message
    const newUserMessage: Message = {
      messageId: generateUuid(),
      charOrUserId: char?.charId,
      role: 'user',
      action: '',
      name: char?.charName || '',
      message: text,
      messageTimestampUtc: Date.now(),
      poseUrl: char?.pose?.['original'] || '',
    }

    return newUserMessage
  }

  async function handleSendMessage(
    text: string,
    chat: Chat,
    isPreview: boolean = false,
  ) {
    hasError.value = false
    isPause.value = false
    preloadedAudios.value = []
    originalTexts.value = ''

    const messageIdPrefix = generateUuid()
    try {
      const user = chat.userChar

      const chatsWithNewUserMessage = [...messages.value]
      if (text.length > 0) {
        const newUserMessage = generateUserMessage(text, user!)
        chatsWithNewUserMessage.push(newUserMessage)
        // Append the user avatar to the avatar position
        const userMessageAvatarPos = getAvatarsPosition(
          newUserMessage,
          avatarPositions.value[avatarPositions.value.length - 1],
        )
        if (userMessageAvatarPos) {
          avatarPositions.value.push(userMessageAvatarPos)
        }
      }
      // Append user message
      messages.value = chatsWithNewUserMessage
      // Avoid the streaming not changing new message
      lastMessagePosition.value = messages.value.length
      lastMessageIdPrefix.value = messageIdPrefix
      lastOriginalMessages.value = messages.value
      lastChat.value = chat
      responseDone.value = false
      chatRoundEnd.value = false
      finalText.value = ''
      await nextTick()
      queue.value = new Queue<string>()
      const eventOrder = chat.universe.events.length - 1
      let count = 0

      // change status to waiting for response showing dots loading animation
      loading.value = true
      const payload: GetCharResponsesPayload = {
        universeId: chat.universe.universeId,
        eventOrder,
        preview: isPreview,
        chatHistory: mapMessageToXML(messages.value),
        enableVoice: debugStore.enableVoice && !debugStore.storyTellerMode,
      }

      autoPlay.value = true

      await chatService.getCharResponsesStream(payload, (chunk) => {
        originalTexts.value += chunk

        if (queue.value) {
          chunk.split('').forEach((c) => {
            if (c) {
              queue.value?.enqueue(c)
            }
          })
        }
        if (count === 0) {
          lastMessageIdPrefix.value = messageIdPrefix
          lastOriginalMessages.value = chatsWithNewUserMessage
          lastChat.value = chat
          dequeue(chat, messageIdPrefix, chatsWithNewUserMessage)
          count += 1
        }
      })
    }
    catch (error) {
      console.error(error)
      hasError.value = true

      // remove the message including the last user message and messages after that
      const lastUserMessageIndex = messages.value.findLastIndex(
        msg => msg.role === 'user',
      )
      if (lastUserMessageIndex !== -1) {
        messages.value = messages.value.slice(0, lastUserMessageIndex)
      }
      // make sure the avatar position is the same as the message
      avatarPositions.value = avatarPositions.value.slice(
        0,
        messages.value.length,
      )
      loading.value = false
    }
    finally {
      responseDone.value = true
    }
  }

  // 生成在create故事之后的首次对话
  async function handleGenerateEventStory(
    chat: Chat,
    isPreview: boolean = false,
  ) {
    hasError.value = false
    isPause.value = false
    preloadedAudios.value = []
    originalTexts.value = ''
    autoPlay.value = true

    const messageIdPrefix = generateUuid()
    try {
      // Avoid the streaming not changing new message
      lastMessagePosition.value = messages.value.length
      responseDone.value = false
      chatRoundEnd.value = false
      finalText.value = ''
      await nextTick()
      queue.value = new Queue<string>()
      let count = 0

      // change status to waiting for response showing dots loading animation
      loading.value = true

      const payload: GenerateEventPayload = {
        universeId: chat.universe.universeId,
        eventOrder: chat.universe.events.length - 1,
        chatHistory: mapMessageToXML(messages.value),
        preview: isPreview,
      }

      await chatService.getEventStoryStream(payload, (chunk) => {
        originalTexts.value += chunk

        if (queue.value) {
          chunk.split('').forEach((c) => {
            if (c) {
              queue.value?.enqueue(c)
            }
          })
        }
        if (count === 0) {
          lastMessageIdPrefix.value = messageIdPrefix
          lastOriginalMessages.value = messages.value
          lastChat.value = chat
          dequeue(chat, messageIdPrefix, messages.value)
          count += 1
        }
      })
    }
    catch (error) {
      console.error(error)
      hasError.value = true
      loading.value = false
    }
    finally {
      responseDone.value = true
    }
  }

  /**
   * handle the stream messages
   * @param chat Chat
   * @param messageIdPrefix  string
   * @param originalMessages Message[]
   */
  async function dequeue(
    chat: Chat,
    messageIdPrefix: string,
    originalMessages: Message[],
  ) {
    // 业务逻辑
    if (queue.value === undefined) {
      console.warn(`[chats => dequeue()] chatId: ${chat.chatId} undefined`)
      return
    }
    // 是否不进入下一个timeout，也就是不dequeue下一个字符
    let willPause = false
    // 清除状态
    clearTimeout(timerId.value)
    if (isPause.value) {
      // 如果是暂停状态，直接返回
      return
    }

    // 退出条件
    if (responseDone.value && queue.value?.isEmpty()) {
      console.log(`[useStream] dequeue() chatId: ${chat.chatId} finished`)
      resetSavedInfo()
      return
    }
    let dequeueInterval = debugStore.streamSpeed

    if (skipToNextMsg.value) {
      // 处理非自动播放状态下，点击进入下一条消息的情况
      let shouldInvokeCb = !isStreaming.value
      lockTap.value = true
      let minPos = findNextSkipPos()
      console.debug(minPos)
      if (minPos === -1) {
        minPos = queue.value.size()
        shouldInvokeCb = false
      }
      // 如果当前存在一个closing tag那就直接dequeue到那个closing之前
      for (let i = 0; i < minPos; i++) {
        finalText.value += queue.value.dequeue()
      }

      await nextTick()

      if (afterSkipCallback.value && shouldInvokeCb) {
        afterSkipCallback.value()
      }
      skipToNextMsg.value = false
      willPause = true
      lockTap.value = false
    }
    else {
      const c = queue.value.dequeue()

      // 处理正常的自动播放stream播放状态
      if (c === '<') {
        const lastMessage = messages.value[messages.value.length - 1]
        if (!autoPlay.value && lastMessage.message.length > 0) {
          finalText.value += c
          while (queue.value.peek() !== undefined && queue.value.peek() !== '>') {
            finalText.value += queue.value.dequeue()
          }
          // 保证可以读取到完整的tag
          finalText.value += queue.value.dequeue()
          isStreaming.value = false
          willPause = true
        }
        else {
          isStreaming.value = true
          let tag = c
          while (queue.value.peek() !== undefined && queue.value.peek() !== '>') {
            tag += queue.value.dequeue()
          }
          // 保证可以读取到完整的tag
          tag += queue.value.dequeue()

          // if the finalText is empty, so it means we are entering the next message
          // increase the dequeue interval to has the user finish reading the message
          if (isCloseTag(tag, ['character', 'narrator', 'say'])) {
            console.debug('[useStream] Wait for audio')
            // use while and sleep to until the isPlaying is false
            while (audioPlaying.value) {
              await sleep(50)
            }
            console.debug('[useStream] Audio finished')

            // check if the lastest message has audio
            dequeueInterval = lastMessage?.audioUrl
              ? 200
              : TEXT_DEQUEUE_INTERVAL_LONG
          }

          // End of this round
          if (isCloseTag(tag, ['chat']) && !chatRoundEnd.value) {
            chatRoundEnd.value = true
          }

          finalText.value += tag
        }
      }
      else if (c !== undefined) {
        isStreaming.value = true
        finalText.value += c
      }
    }

    // Check if chat.chars exists before calling textToMessagesNew
    console.debug('[dequeue] Checking chat.chars:', chat.chars)
    if (!chat.chars || chat.chars.length === 0) {
      console.debug('[dequeue] chat.chars is undefined or empty')
      loading.value = false
      // Just continue streaming but don't try to parse messages
      timerId.value = setTimeout(() => {
        dequeue(chat, messageIdPrefix, originalMessages)
      }, dequeueInterval) as any
      return
    }

    const charMessages = textToMessagesNew(
      finalText.value,
      chat.chars,
      messageIdPrefix,
    )

    // when the charMessages is not empty, change the waitForResponse status
    if (charMessages.length > 0) {
      loading.value = false
    }

    messages.value = [...originalMessages, ...charMessages]

    // Handle the avatars position
    messages.value.forEach((msg, index) => {
      if (
        avatarPositions.value[index]
        && checkMsgIdExist(avatarPositions.value[index]!, msg.messageId)
      ) {
        return
      }
      const lastPosition
        = index === 0 ? undefined : avatarPositions.value[index - 1]
      const newAvatarPosition = getAvatarsPosition(msg, lastPosition)
      if (newAvatarPosition) {
        if (avatarPositions.value[index]) {
          avatarPositions.value[index] = newAvatarPosition
        }
        else {
          avatarPositions.value.push(newAvatarPosition)
        }
      }
    })

    await nextTick()

    // 再次调用
    timerId.value = setTimeout(() => {
      dequeue(chat, messageIdPrefix, originalMessages)
    }, dequeueInterval) as any

    // Determined by the tag type, currently only video will pause the stream until video finished
    if (willPause) {
      lastMessageIdPrefix.value = messageIdPrefix
      lastOriginalMessages.value = messages.value
      lastChat.value = chat
      finalText.value = ''
      pause()
    }
  }

  async function streamInitMessages(chat: Chat) {
    console.debug(`[streamInitMessages] chatId: ${chat.chatId}`)

    // Log and check if chat and universe exist
    if (!chat || !chat.universe) {
      console.debug('[streamInitMessages] chat or chat.universe is undefined')
      return
    }

    console.debug(
      `[streamInitMessages] initMessages: ${chat.universe.initMessages?.substring(0, 50)}...`,
    )

    const msgPrefixId = generateUuid()
    // Only proceed if initMessages exists
    if (!chat.universe.initMessages) {
      console.debug('[streamInitMessages] initMessages is undefined')
      // Set empty messages if initMessages is undefined
      originalTexts.value = ''
      queue.value = new Queue()
      return
    }

    // enqueue the rest message
    const initMessages = `<chat>${chat.universe.initMessages}</chat>`
    originalTexts.value = initMessages
    enqueueMsgChunk(initMessages)

    lastMessageIdPrefix.value = msgPrefixId
    lastOriginalMessages.value = messages.value
    lastChat.value = chat
    finalText.value = ''
    isPause.value = false
    dequeue(chat, msgPrefixId, messages.value)
  }

  function enqueueMsgChunk(chunk: string) {
    console.debug('[enqueueMsgChunk]')
    queue.value = new Queue()

    chunk.split('').forEach((c) => {
      if (c) {
        queue.value!.enqueue(c)
      }
    })
    responseDone.value = true
  }

  function setSkipToNextMsg(value: boolean, cb?: () => void) {
    skipToNextMsg.value = value
    if (cb && !autoPlay.value) {
      afterSkipCallback.value = cb
    }
  }

  function reset() {
    console.debug('[useStream] reset')
    messages.value = []
    avatarPositions.value = []
    loading.value = false
    responseDone.value = false
    finalText.value = ''
    chatRoundEnd.value = false
    isPause.value = false
    hasError.value = false
    queue.value = undefined
    autoPlay.value = false
    clearTimeout(timerId.value)

    lastChat.value = undefined
    lastMessageIdPrefix.value = ''
    lastOriginalMessages.value = []
  }

  function findNextSkipPos() {
    const sayCloseTag = '</say>'
    const charCloseTag = '</character>'
    const narratorCloseTap = '</Narrator>'

    const nextSayCloseTagPos = queue.value!.findPattern(sayCloseTag)
    const nextCharCloseTagPos = queue.value!.findPattern(charCloseTag)
    const nextNarratorCloseTagPos = queue.value!.findPattern(narratorCloseTap)
    // print all the positions
    let minPos = -1
    // check if the two closing tags are successive or not
    if (nextSayCloseTagPos.found && nextCharCloseTagPos.found) {
      const posDiff
        = nextSayCloseTagPos.position - nextCharCloseTagPos.position
      if (Math.abs(posDiff) === sayCloseTag.length + 1) {
        minPos = nextCharCloseTagPos.position + charCloseTag.length
      }
      else {
        minPos = Math.min(
          nextSayCloseTagPos.position + sayCloseTag.length,
          nextCharCloseTagPos.position + charCloseTag.length,
        )
      }
    }
    else if (nextSayCloseTagPos.found) {
      minPos = nextSayCloseTagPos.position + sayCloseTag.length
    }
    else if (nextCharCloseTagPos.found) {
      minPos = nextCharCloseTagPos.position + charCloseTag.length
    }

    if (nextNarratorCloseTagPos.found) {
      minPos = minPos === -1
        ? nextNarratorCloseTagPos.position + narratorCloseTap.length
        : Math.min(
          nextNarratorCloseTagPos.position + narratorCloseTap.length,
          minPos,
        )
    }
    return minPos
  }

  return {
    queue,
    messages,
    avatarPositions,
    originalTexts,
    lastMessagePosition,
    initMessages,
    handleSendMessage,
    handleGenerateEventStory,
    streamInitMessages,
    dequeue,
    loading,
    responseDone,
    finalText,
    chatRoundEnd,
    audioPlaying,
    autoPlay,
    setAudioPlay,

    setSkipToNextMsg,
    lockTap,

    pause,
    resume,
    hasError,

    reset,
  }
}
